import React from 'react'
import { filesize } from 'filesize'
import { T } from '@deltachat/jsonrpc-client'

import { runtime } from '@deltachat-desktop/runtime-interface'
import { BackendRemote } from '../../backend-com'
import { isImage } from '../attachment/Attachment'
import { formatDuration } from '../dialogs/MessageDetail/BasicMessageInfo'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import Icon from '../Icon'
import Button from '../Button'
import { formatAudioTime } from '../AudioPlayer/utils'
import { useSettingsStore } from '../../stores/settings'

import styles from './styles.module.scss'
import audioStyles from '../AudioPlayer/styles.module.scss'
import {
  getPostMessageViewType,
  placeholderCaption,
} from '../../utils/downloadPlaceholder'

export { getPostMessageViewType, placeholderCaption }

// Telegram-style circular download indicator: spins while indeterminate
// (no DownloadProgress event yet), then the arc fills with the percentage.
const RING_R = 16
const RING_C = 2 * Math.PI * RING_R

function ProgressCircle({
  percent,
  label,
}: {
  percent: number | null
  label: string
}) {
  const indeterminate = percent === null
  return (
    <div
      className='download-progress-circle'
      role='progressbar'
      aria-label={label}
      aria-valuenow={indeterminate ? undefined : percent}
    >
      <svg viewBox='0 0 40 40' className={indeterminate ? 'indeterminate' : ''}>
        <circle className='track' cx='20' cy='20' r={RING_R} />
        <circle
          className='bar'
          cx='20'
          cy='20'
          r={RING_R}
          strokeDasharray={RING_C}
          strokeDashoffset={
            indeterminate ? RING_C * 0.75 : RING_C * (1 - percent / 100)
          }
        />
      </svg>
      {!indeterminate && <span className='pct'>{percent}%</span>}
    </div>
  )
}

/**
 * The preview thumbnail, or the grey fallback box when there is none — or when the blob has
 * gone missing (housekeeping GC, or an OPFS write-through lost across a reload), which would
 * otherwise render as the browser's broken-image glyph inside the bubble.
 */
function Preview({
  file,
  className,
}: {
  file: string | null | undefined
  className?: string
}) {
  const [broken, setBroken] = React.useState(false)
  return file && !broken ? (
    <img
      className={className}
      src={runtime.transformBlobURL(file)}
      alt=''
      onError={() => setBroken(true)}
    />
  ) : (
    <div className='download-on-demand-empty' />
  )
}

function CircleDownloadButton({
  messageId,
  accountId,
  tabIndex,
}: {
  messageId: number
  accountId: number
  tabIndex: -1 | 0
}) {
  const tx = useTranslationFunction()
  return (
    <button
      type='button'
      className='circle-download-button'
      aria-label={tx('download')}
      onClick={() =>
        BackendRemote.rpc.downloadFullMessage(accountId, messageId)
      }
      tabIndex={tabIndex}
    >
      <Icon icon='download' size={18} />
    </button>
  )
}

type PlaceholderProps = {
  message: T.Message
  postMessageViewType: T.Viewtype
  accountId: number
  /** No caption below the preview; with one, upstream's content-below class handles spacing/corners. */
  noCaption?: boolean
  /** Live percentage (0-100) from the DownloadProgress event, null before the first event. */
  downloadPercent: number | null
  tabindexForInteractiveContents: -1 | 0
}

function DownloadControls({
  message,
  accountId,
  downloadPercent,
  tabindexForInteractiveContents,
}: Omit<PlaceholderProps, 'postMessageViewType'>) {
  const tx = useTranslationFunction()
  const { downloadState } = message
  return (
    <div className='download-on-demand-controls'>
      {downloadState === 'Failure' && (
        <span className='failed'>{tx('download_failed')}</span>
      )}
      {downloadState === 'InProgress' ? (
        <ProgressCircle percent={downloadPercent} label={tx('downloading')} />
      ) : (
        <CircleDownloadButton
          messageId={message.id}
          accountId={accountId}
          tabIndex={tabindexForInteractiveContents}
        />
      )}
      <span className='size'>{filesize(message.fileBytes ?? 0)}</span>
    </div>
  )
}

/**
 * Styled placeholder shown for a download-on-demand pre-message while
 * `downloadState !== 'Done'`. It mirrors the "Done"-path markup/classes from
 * `messageAttachment.tsx` (`.message-attachment-media`, `-audio`, `-generic`,
 * `.webxdc`) but swaps the real attachment for a blurred thumbnail (or a grey
 * box) plus a prominent download button and the target file size.
 */
export default function DownloadOnDemandPlaceholder({
  message,
  postMessageViewType,
  accountId,
  downloadPercent,
  noCaption,
  tabindexForInteractiveContents,
}: PlaceholderProps) {
  const tx = useTranslationFunction()
  const customAudioControls =
    useSettingsStore()[0]?.desktopSettings.experimentalAudioPlayerControls ===
    true
  // Upstream's content-below class neutralises the full-bleed negative bottom
  // margin and squares the bottom corners whenever a caption follows - reuse it
  // instead of reinventing the spacing.
  const contentBelow = noCaption ? '' : ' content-below'
  const mediaBoxClass =
    'message-attachment-media download-on-demand' + contentBelow
  const controls = (
    <DownloadControls
      message={message}
      accountId={accountId}
      downloadPercent={downloadPercent}
      tabindexForInteractiveContents={tabindexForInteractiveContents}
    />
  )

  const viewType = postMessageViewType

  // Image / Gif / Sticker: blurred thumbnail (or grey box) with centered overlay.
  if (isImage(viewType) || viewType === 'Sticker') {
    return (
      <div className={mediaBoxClass}>
        <Preview file={message.file} className='attachment-content' />
        <div className='download-on-demand-overlay'>{controls}</div>
      </div>
    )
  }

  // Video: same media box, black background, and a label — "Video · 0:23" —
  // where a play glyph would go. A glyph would invite a click that cannot do
  // anything, since a pre-message has no video to play until it is downloaded,
  // and being centered it would sit on top of the length. Senders that predate
  // `Chat-Duration` leave the duration at 0; the label is then just the type.
  if (viewType === 'Video') {
    return (
      <div className={mediaBoxClass + ' video'}>
        <Preview file={message.file} className='attachment-content' />
        <div className='download-on-demand-overlay'>
          <span className='media-label'>
            {message.duration > 0
              ? `${tx('video')} · ${formatDuration(message.duration)}`
              : tx('video')}
          </span>
          {controls}
        </div>
      </div>
    )
  }

  // Audio / Voice: mimic whichever player the message will turn into, so the
  // card doesn't reshape itself on download. Both variants put the download
  // circle in the play slot and show the duration and the size; no filename,
  // neither player shows one.
  if (viewType === 'Audio' || viewType === 'Voice') {
    const downloadControl =
      message.downloadState === 'InProgress' ? (
        <ProgressCircle percent={downloadPercent} label={tx('downloading')} />
      ) : (
        <CircleDownloadButton
          messageId={message.id}
          accountId={accountId}
          tabIndex={tabindexForInteractiveContents}
        />
      )
    return (
      <div
        className={'message-attachment-audio download-on-demand' + contentBelow}
      >
        {customAudioControls ? (
          // Custom player (experimentalAudioPlayerControls): borrow its own
          // layout classes so the placeholder inherits the real geometry
          // (300px floor, 70vw on phones, 32px waveform row) instead of
          // re-deriving it. The waveform is flat — there is no audio to
          // analyse yet — and the size takes the speed pill's slot, since a
          // speed control would be a button that cannot do anything.
          <div className={audioStyles.customControls + ' fake-custom-player'}>
            {downloadControl}
            <div className={audioStyles.stack}>
              <div className={audioStyles.seekWrap}>
                <div className='fake-waveform' aria-hidden='true' />
              </div>
              <div className={audioStyles.metaRow}>
                <span className={audioStyles.time}>
                  {formatAudioTime(0)} /{' '}
                  {message.duration > 0
                    ? formatAudioTime(message.duration / 1000)
                    : '–:––'}
                </span>
                <span className='size'>{filesize(message.fileBytes ?? 0)}</span>
              </div>
            </div>
          </div>
        ) : (
          // Native <audio controls> bar: a pill with the duration, a fake
          // track and the size.
          <div className='fake-audio-player'>
            {downloadControl}
            <span className='time'>
              {message.duration > 0 ? formatDuration(message.duration) : '–:––'}
            </span>
            <div className='fake-track' aria-hidden='true' />
            <span className='size'>{filesize(message.fileBytes ?? 0)}</span>
          </div>
        )}
        {message.downloadState === 'Failure' && (
          <span className='failed'>{tx('download_failed')}</span>
        )}
      </div>
    )
  }

  // Webxdc: the app card, with the app icon (thumbnail) or default asset.
  if (viewType === 'Webxdc') {
    return (
      <div className={'webxdc download-on-demand' + contentBelow}>
        {/* No thumbnail yet and no .xdc downloaded to derive an icon from, so the fallback is
            the grey box rather than getWebxdcIconURL (which is unimplemented on
            target-browser and can't work for a pre-message). */}
        <Preview file={message.file} />
        <div className='info-text'>
          <div className='name'>{message.fileName}</div>
        </div>
        {message.downloadState === 'Failure' && (
          <span className='failed'>{tx('download_failed')}</span>
        )}
        {message.downloadState === 'InProgress' ? (
          <ProgressCircle percent={downloadPercent} label={tx('downloading')} />
        ) : (
          // Mirror the real card's big primary "Start" button, labeled Download.
          <Button
            className={styles.startWebxdcButton}
            styling='primary'
            onClick={() =>
              BackendRemote.rpc.downloadFullMessage(accountId, message.id)
            }
            tabIndex={tabindexForInteractiveContents}
          >
            {tx('download')}
          </Button>
        )}
        <span className='size'>{filesize(message.fileBytes ?? 0)}</span>
      </div>
    )
  }

  // Generic file (and any other type): the real card's layout, but the round
  // download button / progress ring sits where the file icon would be.
  return (
    <div
      className={'message-attachment-generic download-on-demand' + contentBelow}
    >
      {message.downloadState === 'InProgress' ? (
        <ProgressCircle percent={downloadPercent} label={tx('downloading')} />
      ) : (
        <CircleDownloadButton
          messageId={message.id}
          accountId={accountId}
          tabIndex={tabindexForInteractiveContents}
        />
      )}
      <div className='text-part'>
        <div className='name'>{message.fileName}</div>
        <div className='size'>{filesize(message.fileBytes ?? 0)}</div>
        {message.downloadState === 'Failure' && (
          <span className='failed'>{tx('download_failed')}</span>
        )}
      </div>
    </div>
  )
}
